import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Loader, Segment } from 'semantic-ui-react';
import { Notification, STATUS_TYPES } from 'dyl-components';

import onboardingActions from 'actions/onboarding';
import { useConfirm } from 'shared/confirmation/useConfirm';
import MessageTemplates from './MessageTemplates';
import FollowUpSequence from './FollowUpSequence';
import ConfirmMessaging from './ConfirmMessaging';
import { MAX_STEPS_PER_TYPE, MAX_TIME_AMOUNT, SEQUENCE_STEP_TYPES, getUnknownMergeFields, hasOptOutLanguage } from './messagingUtils';
import { TEXTING_AUTOMATION_SCREENS, getOptionText } from '../constants';
import { findDuplicateIds, getLengthError, hasErrors } from '../validationUtils';

const MANUAL_TRIGGER = 'manual';
const DEFAULT_PIPELINE_TRIGGER = 'default_new_lead';

const getMergeFieldError = (text) => {
    const unknown = getUnknownMergeFields(text);
    return unknown.length ? `Unknown merge field ${unknown.join(', ')}. Use Insert Field to pick one.` : null;
}

const collectErrors = (items, validate) => {
    const duplicateIds = findDuplicateIds(items, ({ name }) => name);
    return items.reduce((result, item) => {
        const itemErrors = validate(item);
        if (duplicateIds.includes(item.id) && !itemErrors.name) {
            itemErrors.name = 'Template names must be unique';
        }
        return Object.keys(itemErrors).length ? { ...result, [item.id]: itemErrors } : result;
    }, {});
}

// Name limits mirror TEXT_TEMPLATE_NAME / EMAIL_TEMPLATE_NAME; subject limit mirrors the Email Template modal
const validateTemplates = (textTemplates, emailTemplates) => {
    const textErrors = collectErrors(textTemplates, ({ name, message }) => {
        const errors = {};
        const nameError = getLengthError(name, 'Template name', 2, 64);
        const messageError = getLengthError(message, 'Message', 1, 1600) || getMergeFieldError(message);
        if (nameError) errors.name = nameError;
        if (messageError) errors.message = messageError;
        return errors;
    });
    const emailErrors = collectErrors(emailTemplates, ({ name, subject, body }) => {
        const errors = {};
        const nameError = getLengthError(name, 'Template name', 2, 64);
        const subjectError = getLengthError(subject, 'Subject', 1, 256) || getMergeFieldError(subject);
        const bodyError = getLengthError(body, 'Body', 1, 10000) || getMergeFieldError(body);
        if (nameError) errors.name = nameError;
        if (subjectError) errors.subject = subjectError;
        if (bodyError) errors.body = bodyError;
        return errors;
    });
    return {
        ...(hasErrors(textErrors) ? { texts: textErrors } : {}),
        ...(hasErrors(emailErrors) ? { emails: emailErrors } : {})
    };
}

// Limits mirror shared/schemas/sequenceValidationSchema.js
const validateSequence = (sequence, templateIdsByType) => {
    if (!sequence.included) {
        return {};
    }
    const errors = {};
    const nameError = getLengthError(sequence.name, 'Sequence name', 2, 64);
    if (nameError) {
        errors.name = nameError;
    }
    if (sequence.description.trim()) {
        const descriptionError = getLengthError(sequence.description, 'Description', 3, 256);
        if (descriptionError) {
            errors.description = descriptionError;
        }
    }

    const stepErrors = sequence.steps.reduce((result, step, index) => {
        const stepError = {};
        if (step.deliver === 'instant') {
            if (index > 0) {
                stepError.timing = 'Only the first step can be immediate';
            }
        } else {
            const max = MAX_TIME_AMOUNT[step.deliver];
            if (!Number.isInteger(step.timeamount) || step.timeamount < 1 || step.timeamount > max) {
                stepError.timing = `Enter a whole number from 1 to ${max}`;
            }
        }
        if (step.type !== 'call' && !(templateIdsByType[step.type] || []).includes(step.template_id)) {
            stepError.template = 'Choose an included template';
        }
        return Object.keys(stepError).length ? { ...result, [step.id]: stepError } : result;
    }, {});
    if (hasErrors(stepErrors)) {
        errors.steps = stepErrors;
    }

    const overLimitTypes = SEQUENCE_STEP_TYPES.filter(({ value }) => sequence.steps.filter(({ type }) => type === value).length > MAX_STEPS_PER_TYPE);
    if (overLimitTypes.length) {
        errors.steps_summary = `A sequence can have at most ${MAX_STEPS_PER_TYPE} steps of each type. Remove some ${overLimitTypes.map(({ text }) => text.toLowerCase()).join(' and ')} steps.`;
    }
    return errors;
}

const TextingAutomationStep = ({ businessProfileDraft, crmDraft, draft, onDraftChange, onBack, onComplete }) => {
    const dispatch = useDispatch();
    const { isConfirmed } = useConfirm();

    const { user_id, isGeneratingMessagingSetup } = useSelector(state => ({
        user_id: state.auth.user_id,
        isGeneratingMessagingSetup: state.onboarding.isGeneratingMessagingSetup
    }));

    const [screen, setScreen] = useState(draft?.approval ? TEXTING_AUTOMATION_SCREENS.CONFIRM : (draft?.screen || TEXTING_AUTOMATION_SCREENS.TEMPLATES));
    const [textTemplates, setTextTemplates] = useState(draft?.text_templates || null);
    const [emailTemplates, setEmailTemplates] = useState(draft?.email_templates || null);
    const [sequence, setSequence] = useState(draft?.sequence || null);
    const [trigger, setTrigger] = useState(draft?.trigger || null);
    const [explanations, setExplanations] = useState(draft?.explanations || {});
    const [generatedFor, setGeneratedFor] = useState(draft?.generated_for || null);
    const [approval, setApproval] = useState(draft?.approval || null);

    // Step 1 choices decide which messages start switched on
    const profile = businessProfileDraft?.profile || {};
    const businessRecommendationIds = (businessProfileDraft?.recommendations || []).map(({ id }) => id);
    const businessSelected = businessProfileDraft?.selected || [];
    const wasChosen = (id) => !businessRecommendationIds.includes(id) || businessSelected.includes(id);
    const inputsJSON = JSON.stringify({
        industry: profile.industry || '',
        include_new_lead_text: wasChosen('new_lead_text_response'),
        include_follow_up: wasChosen('automated_follow_up')
    });
    const isStale = !!generatedFor && JSON.stringify(generatedFor) !== inputsJSON;

    // Sequences start when a record enters a pipeline stage, so offer the lead stages from CRM Configuration
    const leadPipeline = crmDraft?.approval && crmDraft.pipelines?.lead?.included ? crmDraft.pipelines.lead : null;
    const triggerOptions = [
        ...(leadPipeline
            ? leadPipeline.stages.map(({ id, name }) => ({ key: id, value: id, text: `When a lead enters "${name}" (${leadPipeline.name})` }))
            : [{ key: DEFAULT_PIPELINE_TRIGGER, value: DEFAULT_PIPELINE_TRIGGER, text: 'When a lead enters "New Lead" (default lead pipeline)' }]),
        { key: MANUAL_TRIGGER, value: MANUAL_TRIGGER, text: "Don't start automatically. I'll add contacts myself." }
    ];
    const triggerOptionValues = triggerOptions.map(({ value }) => value).join(',');
    const defaultTrigger = triggerOptions[0].value;

    // Keep the trigger valid if pipeline stages changed in CRM Configuration
    useEffect(() => {
        if (!triggerOptionValues.split(',').includes(trigger)) {
            setTrigger(defaultTrigger);
        }
    }, [trigger, triggerOptionValues, defaultTrigger]);

    const generateSetup = useCallback(async () => {
        try {
            const inputs = JSON.parse(inputsJSON);
            const result = await dispatch(onboardingActions.generateMessagingSetup(inputs));
            setTextTemplates(result.text_templates);
            setEmailTemplates(result.email_templates);
            setSequence(result.sequence);
            setExplanations(result.explanations);
            setGeneratedFor(inputs);
            setApproval(null);
        } catch (e) {
            Notification.alert('Failed to draft messages', STATUS_TYPES.ERROR);
        }
    }, [dispatch, inputsJSON]);

    // Suggestions are generated automatically the first time the step opens
    const hasSuggestions = !!textTemplates && !!emailTemplates && !!sequence;
    useEffect(() => {
        if (!hasSuggestions) {
            generateSetup();
        }
    }, [hasSuggestions, generateSetup]);

    // An approval only covers what the user saw; any later edit requires approving again
    const approvalSnapshot = JSON.stringify({ textTemplates, emailTemplates, sequence, trigger });
    useEffect(() => {
        if (approval && approval.snapshot !== approvalSnapshot) {
            setApproval(null);
        }
    }, [approval, approvalSnapshot]);

    const getStepDraft = (overrides = {}) => ({
        screen,
        text_templates: textTemplates,
        email_templates: emailTemplates,
        sequence,
        trigger,
        explanations,
        generated_for: generatedFor,
        approval,
        ...overrides
    });

    useEffect(() => {
        onDraftChange({ screen, text_templates: textTemplates, email_templates: emailTemplates, sequence, trigger, explanations, generated_for: generatedFor, approval });
    }, [onDraftChange, screen, textTemplates, emailTemplates, sequence, trigger, explanations, generatedFor, approval]);

    const onRegenerate = async () => {
        const shouldRegenerate = await isConfirmed('Redraft templates?', 'New drafts will replace your templates and follow-up sequence.');
        if (shouldRegenerate) {
            await generateSetup();
        }
    }

    const onConfirm = () => {
        // TODO: send the approved messaging setup to the backend once the endpoint exists
        const newApproval = { approved_by: user_id, approved_at: new Date().toISOString(), snapshot: approvalSnapshot };
        setApproval(newApproval);
        onComplete(getStepDraft({ approval: newApproval }));
        Notification.alert('Messaging setup saved as a draft', STATUS_TYPES.SUCCESS);
    }

    if (!hasSuggestions) {
        return (
            <Segment placeholder textAlign='center'>
                {isGeneratingMessagingSetup ? (
                    <Loader active inline='centered'>Drafting messages and a follow-up sequence for your business…</Loader>
                ) : (
                    <p>We couldn't draft your messages. <Button basic size='small' onClick={generateSetup}>Try Again</Button></p>
                )}
            </Segment>
        );
    }

    const selectedTexts = textTemplates.filter(({ selected }) => selected);
    const selectedEmails = emailTemplates.filter(({ selected }) => selected);
    const templateErrors = validateTemplates(selectedTexts, selectedEmails);
    const templateOptions = {
        text: selectedTexts.map(({ id, name }) => ({ key: id, value: id, text: name || 'Untitled' })),
        email: selectedEmails.map(({ id, name }) => ({ key: id, value: id, text: name || 'Untitled' }))
    };
    const sequenceErrors = validateSequence(sequence, {
        text: templateOptions.text.map(({ value }) => value),
        email: templateOptions.email.map(({ value }) => value)
    });

    const firstTextStep = sequence.included ? sequence.steps.find(({ type }) => type === 'text') : null;
    const firstText = firstTextStep && textTemplates.find(({ id }) => id === firstTextStep.template_id);
    const warnings = firstText && !hasOptOutLanguage(firstText.message)
        ? ['The first text a lead receives should include opt-out language, such as "Reply STOP to opt out."']
        : [];

    const updateItem = (setItems) => (id, changes) => setItems(items => items.map(item => item.id === id ? { ...item, ...changes } : item));
    const removeItem = (setItems) => (id) => setItems(items => items.filter(item => item.id !== id));

    switch (screen) {
        case TEXTING_AUTOMATION_SCREENS.SEQUENCE:
            return (
                <FollowUpSequence
                    sequence={sequence}
                    errors={sequenceErrors}
                    warnings={warnings}
                    explanation={explanations.sequence}
                    trigger={trigger}
                    triggerOptions={triggerOptions}
                    templateOptions={templateOptions}
                    onChange={setSequence}
                    onTriggerChange={setTrigger}
                    canContinue={!hasErrors(sequenceErrors)}
                    onBack={() => setScreen(TEXTING_AUTOMATION_SCREENS.TEMPLATES)}
                    onContinue={() => setScreen(TEXTING_AUTOMATION_SCREENS.CONFIRM)}
                />
            );
        case TEXTING_AUTOMATION_SCREENS.CONFIRM:
            return (
                <ConfirmMessaging
                    textTemplates={selectedTexts}
                    emailTemplates={selectedEmails}
                    sequence={sequence}
                    triggerText={getOptionText(triggerOptions, trigger)}
                    isApprovedInitially={!!approval}
                    onBack={() => setScreen(TEXTING_AUTOMATION_SCREENS.SEQUENCE)}
                    onConfirm={onConfirm}
                />
            );
        default:
            return (
                <Segment basic style={{ padding: 0 }} loading={isGeneratingMessagingSetup}>
                    <MessageTemplates
                        textTemplates={textTemplates}
                        emailTemplates={emailTemplates}
                        errors={templateErrors}
                        explanation={explanations.templates}
                        isStale={isStale}
                        onRegenerate={onRegenerate}
                        onChangeText={updateItem(setTextTemplates)}
                        onChangeEmail={updateItem(setEmailTemplates)}
                        onAddText={() => setTextTemplates([...textTemplates, { id: `text_custom_${Date.now()}`, name: '', purpose: '', message: '', selected: true, is_custom: true }])}
                        onAddEmail={() => setEmailTemplates([...emailTemplates, { id: `email_custom_${Date.now()}`, name: '', purpose: '', subject: '', body: '', selected: true, is_custom: true }])}
                        onRemoveText={removeItem(setTextTemplates)}
                        onRemoveEmail={removeItem(setEmailTemplates)}
                        canContinue={!hasErrors(templateErrors)}
                        onBack={onBack}
                        onContinue={() => setScreen(TEXTING_AUTOMATION_SCREENS.SEQUENCE)}
                    />
                </Segment>
            );
    }
}

export default TextingAutomationStep;
